import FeatureGates, {
  type GetExperimentValueOptions,
} from '@atlaskit/feature-gate-js-client';
import { proxy } from 'valtio';

import {
  isFeatureFlagEnabled,
  type FeatureFlagOverrides,
} from '@canvas/client-feature-flags-state';

/**
 * When creating an experiment, we configure params for that experiment to drive
 * behaviour in code (rather than using hard-coded cohort names)
 *
 * With A/B testing, we can use a simple boolean param (`enabled`) to control
 * behaviour - note that this param has to be configured when you create a new
 * experiment in Statsig.
 *
 * See: https://docs.statsig.com/experiments-plus/create-new#configure-your-groups-and-parameters
 */
export const experimentConfig = {
  /**
   * A/A test for validating our Statsig integration and our metrics instrumentation
   * https://switcheroo.atlassian.com/gates/5f23451e-6414-449b-a04c-e917a07fb929
   */
  whiteboardAATest: {
    experimentId: 'whiteboards-aa-test',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Progressively disables awareness features in canvas proportionate to the number of collaborators/clients.
   * https://switcheroo.atlassian.com/ui/gates/4341b653-3f4c-4a5c-af21-7c6ca9cba2bd/key/cc-content-types-progressive-awareness-disablement
   */
  progressiveAwarenessDisablement: {
    experimentId: 'cc-content-types-progressive-awareness-disablement',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables more shapes
   * https://switcheroo.atlassian.com/ui/gates/0957c8e1-0427-4694-b3f7-39ba65a9132d/key/cc-whiteboards-more-shapes
   */
  moreShapes: {
    experimentId: 'cc-whiteboards-more-shapes',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables mentions
   * https://switcheroo.atlassian.com/ui/gates/be69c3a6-e4b0-4c01-b845-c2e03bc18ef1/key/cc-whiteboards-mentions
   */
  mentions: {
    experimentId: 'cc-whiteboards-mentions',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables an overlay with onboarding action buttons on board creation
   * https://switcheroo.atlassian.com/ui/gates/394d0e3d-e742-4507-a7dd-aa5b8690039c/key/cc-whiteboards-user-onboarding
   */
  onboardingOverlay: {
    experimentId: 'cc-whiteboards-user-onboarding',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Allow users to create webgl rendered cloud icons
   * https://switcheroo.atlassian.com/ui/gates/ce8f9a41-4c01-4fa1-baa2-a661f56eda67/key/cc-whiteboards-cloud-shapes
   */
  cloudShapes: {
    experimentId: 'cc-whiteboards-cloud-shapes',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * A/A test against cloud shapes to verify numbers
   * https://switcheroo.atlassian.com/ui/gates/7b16fbb4-995f-4780-9a85-eb434957ca1d/key/cc-whiteboards-cloud-shapes-aa-test
   */
  cloudShapesAATest: {
    experimentId: 'cc-whiteboards-cloud-shapes-aa-test',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables additional options in the connections import experience
   * https://switcheroo.atlassian.com/ui/gates/3c1109ca-06c8-4431-92c1-f84d6afd82de/key/databases-frontend-connections-more-options-exp
   */
  connectionsMoreOptions: {
    experimentId: 'databases-frontend-connections-more-options-exp',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new UI for the set up panel with more templates in a new tab
   * and import in a separate tab.
   * https://switcheroo.atlassian.com/ui/gates/99582f3d-b833-4898-b093-d1002e73a971/key/databases-frontend-setup-panel-experiment
   */
  setupPanel: {
    experimentId: 'databases-frontend-setup-panel-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new checkbox field type.
   * https://switcheroo.atlassian.com/ui/gates/8a80339d-1133-43e8-8498-996d107ed89f/key/databases-frontend-checkbox-field-type-experiment
   */
  checkboxFieldType: {
    experimentId: 'databases-frontend-checkbox-field-type-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the calendar layout for the databases.
   * https://switcheroo.atlassian.com/ui/gates/aca974aa-cdae-45d4-8967-d86d12de0d05/key/databases-frontend-layout-calendar-experiment
   */
  layoutCalendar: {
    experimentId: 'databases-frontend-layout-calendar-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the board/card layout visual updates for databases.
   * https://switcheroo.atlassian.com/ui/gates/00703e94-6c06-4ec7-9a65-a9f8a79d3a9e/key/databases-frontend-board-card-visual-experiment
   */
  boardCardUpdate: {
    experimentId: 'databases-frontend-board-card-visual-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables 2 way sync of Jira Work items from databases
   * https://switcheroo.atlassian.com/ui/gates/b5bd0131-2b49-4908-8eca-3c272cdc4dac/key/confluence_database_2_way_sync
   */
  databases2waySyncEnabled: {
    experimentId: 'confluence_database_2_way_sync',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/1ceff846-d891-440a-a6f3-3000293886f9/key/show-sidebar-smart-actions
   */
  showSidebarSmartActions: {
    experimentId: 'show-sidebar-smart-actions',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables date range and time for date field settings
   * https://switcheroo.atlassian.com/ui/gates/0fc6a623-4964-4856-b4f2-80b8f3beb1d9/key/databases-frontend-field-settings-date-experiment
   */
  dateFieldSettings: {
    experimentId: 'databases-frontend-field-settings-date-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables various improvements to the Jira Import experience, including moving it to a sidebar,
   * adding filtering and more. Related to NLP for Jira Import picker.
   * https://switcheroo.atlassian.com/ui/gates/61a8ecac-f332-4ed1-9175-a6dbc52cd6e5/key/confluence-whiteboards-improve-jira-search
   */
  shouldUseImprovedJiraImport: {
    experimentId: 'confluence-whiteboards-improve-jira-search',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables display size options for media field settings
   * https://switcheroo.atlassian.com/ui/gates/6ba5a334-54a8-47a6-ad07-c4d66248e271/key/databases-frontend-field-settings-media-experiment
   */
  mediaFieldSettings: {
    experimentId: 'databases-frontend-field-settings-media-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables new board grouping features (subgrouping, group sorting, grouping by non-tag fields).
   * https://switcheroo.atlassian.com/ui/gates/995573d3-e0ee-450c-9f3d-2abba6dfd9c1/key/databases-frontend-grouping-board-experiment
   */
  databasesBoardGrouping: {
    experimentId: 'databases-frontend-grouping-board-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables additional grouping features (e.g. toggling group visibility, grouping by additional field types) for databases board layout.
   * https://switcheroo.atlassian.com/ui/gates/0c545341-20f9-413b-94c3-86110f72fa69/key/databases-frontend-grouping-board-m2-experiment
   */
  databasesBoardGroupingM2: {
    experimentId: 'databases-frontend-grouping-board-m2-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables NLP (Natural Language Processing) for Jira Import picker
   * https://switcheroo.atlassian.com/ui/gates/071f50a4-1f21-4371-9dc9-8dd6c3844a2b/key/confluence-whiteboards-use-nlp-jira-issue-search
   */
  shouldUseNlpForJiraIssueSearch: {
    experimentId: 'confluence-whiteboards-use-nlp-jira-issue-search',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables presence in databases which highlights cells/cards that are being edited/selected by other users
   * https://switcheroo.atlassian.com/ui/gates/2cd58276-802b-45e1-b236-ad5773a9a666/key/databases-frontend-presence-experiment
   */
  databasePresence: {
    experimentId: 'databases-frontend-presence-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new database more menu entry
   * https://switcheroo.atlassian.com/ui/gates/6f25555f-0127-4f61-9541-1a0661f69cff/key/cc-whiteboards-create-database-from-more-menu
   */
  databaseMoreMenuEntry: {
    experimentId: 'cc-whiteboards-create-database-from-more-menu',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Removes the pending logic for creating a new entry from the referencing modal
   * https://switcheroo.atlassian.com/ui/gates/5a6c05cc-29c3-40f4-9dd8-9c9385944756/key/databases-frontend-add-entry-pending-experiment
   */
  removeAddEntryPending: {
    experimentId: 'databases-frontend-add-entry-pending-experiment',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new diagramming agent using an ELK layout engine instead of dagre.
   * https://switcheroo.atlassian.com/ui/gates/aa407bad-b331-4d9d-983f-65dd4273f1c7/key/whiteboards-use-v2-diagramming-agent
   */
  v2DiagrammingAgent: {
    experimentId: 'whiteboards-use-v2-diagramming-agent',
    defaultParams: {
      enabled: false,
    },
  },
  /** Enables random colour option on sticky notes */
  randomStickyColour: {
    experimentId: 'random_sticky_colour',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new AI button in the mini modal
   * https://switcheroo.atlassian.com/ui/gates/225bbb2b-ee14-4242-b3af-74794a11b89b/key/rovo_chat_refresh_v1_experiment
   */
  miniModalAIButton: {
    experimentId: 'rovo_chat_refresh_v1_experiment',
    defaultParams: {
      enabled: false,
      isWhiteboardRovoButtonEnabled: false,
    },
  },

  /**
   * Enables the Jira Unfurl button in the element toolbar
   * https://switcheroo.atlassian.com/ui/gates/b3c5f3a6-cfe1-4636-bcfd-2d6c58a1d24b/key/cc-whiteboards-jira-unfurl
   */
  whiteboardsJiraUnfurl: {
    experimentId: 'cc-whiteboards-jira-unfurl',
    defaultParams: {
      enabled: false,
    },
  },

  /**
   * Enables consistent comment editor placeholders as part of Project Cohiti https://product-fabric.atlassian.net/browse/CPC-72
   * https://switcheroo.atlassian.com/ui/gates/310e89e8-3189-41c5-bcfc-7898f28a3b7e/key/confluence_comments_cohiti_consistent_placeholders
   */
  standardizedCommentPlaceholder: {
    experimentId: 'confluence_comments_cohiti_consistent_placeholders',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Reopen the templates modal when the user performs an undo immediately after inserting a template
   * https://switcheroo.atlassian.com/ui/gates/8da14f9d-f875-4dab-800a-33a26e3606be/key/whiteboards-template-insertion-undo-behaviour
   */
  templateInsertionUndoBehaviour: {
    experimentId: 'whiteboards-template-insertion-undo-behaviour',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Allows you to select the quality of exporting a whiteboard
   * https://switcheroo.atlassian.com/ui/gates/fede86d6-e54e-4340-a218-cd3a4f6cd144/key/whiteboard_high_quality_export
   */
  whiteboardHighQualityExport: {
    experimentId: 'whiteboard_high_quality_export',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the magic pen feature
   * https://switcheroo.atlassian.com/ui/gates/dfdc361a-acfe-45df-9a6e-103048146be5/key/cc-whiteboards-magic-pen
   */
  magicPen: {
    experimentId: 'cc-whiteboards-magic-pen',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new diagramming arrowheads feature
   * https://switcheroo.atlassian.com/ui/gates/84ecd076-717f-47de-9092-d41ffc1e1d5f/key/cc-whiteboards-diagramming-arrowheads
   */
  diagrammingArrowheads: {
    experimentId: 'cc-whiteboards-diagramming-arrowheads',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables quick object transformation (e.g. from sticky -> textbox)
   * https://switcheroo.atlassian.com/ui/gates/c095ca94-e548-4416-a275-302d49024d2c/key/whiteboards-quick-object-transform
   */
  quickTransform: {
    experimentId: 'whiteboards-quick-object-transform',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables the new UML shapes feature
   * https://switcheroo.atlassian.com/ui/gates/d3d9c1ad-cfec-4efc-8124-fc38e6822fc8/key/cc-whiteboards-uml-shapes
   */
  umlShapes: {
    experimentId: 'cc-whiteboards-uml-shapes',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * A/A test for October 25th for ALL whiteboards loads - ignores the existence of the
   * holdout, i.e. absolutely 100% of exposures
   * https://switcheroo.atlassian.com/ui/gates/110c9054-7e7b-4f17-a885-d1a6aca27e5d/key/cc-whiteboards-october-25-aa-all
   */
  october25AaAll: {
    experimentId: 'cc-whiteboards-october-25-aa-all',
    defaultParams: {
      enabled: false,
    },
  },

  /**
   * A/A test for October 25th for whiteboards loads in holdout - WITH the
   * product-cc-tenant_id-fy26h1-tenant-user-gate mismatch - gate target
   * https://switcheroo.atlassian.com/ui/gates/795fa67c-9c44-4eb4-a149-b6f03907e3ab/key/cc-whiteboards-october-25-aa-all-holdout
   */
  october25AaAllHoldout: {
    experimentId: 'cc-whiteboards-october-25-aa-all-holdout',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * A/A test for October 25th for Jira import loads - WITH holdout
   * https://switcheroo.atlassian.com/ui/gates/02f2c458-eae9-4a40-91ba-2beb2cf24416/key/cc-whiteboards-october-25-aa-jira-import
   */
  october25AaJiraImport: {
    experimentId: 'cc-whiteboards-october-25-aa-jira-import',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * UX health week Dec 2025!!
   * Batch all your UX health week changes here.
   *
   * tenantId based targeting.
   *
   * https://switcheroo.atlassian.com/ui/gates/d5f852b1-9ec4-4c0b-989b-edb64e252277/key/cc-whiteboards-ux-health-dec-2025
   * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/cc-whiteboards-ux-health-dec-2025/results
   */
  uxHealthDec2025: {
    experimentId: 'cc-whiteboards-ux-health-dec-2025',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables per-element-type sizing strategies
   * https://switcheroo.atlassian.com/ui/gates/9f107a97-74ad-45db-a08a-3ee03749bacb/key/cc-whiteboards-sizing-refactor
   */
  sizingRefactor: {
    experimentId: 'cc-whiteboards-sizing-refactor',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Adds the ability to attach multiple line labels to a path
   * https://switcheroo.atlassian.com/ui/gates/a583606d-e37d-4754-b3bf-5fc3f0b8d762/key/whiteboards-multi-path-labels
   */
  multiPathLabels: {
    experimentId: 'whiteboards-multi-path-labels',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Enables contextual UI side effects when specific templates are inserted
   * https://switcheroo.atlassian.com/ui/gates/73b44d17-863b-4a64-b64b-bd2f5cc604e4/key/whiteboards-template-insertion-contextual-shapes
   */
  whiteboardsTemplateInsertionContextualShapes: {
    experimentId: 'whiteboards-template-insertion-contextual-shapes',
    defaultParams: {
      variant: 'control' as 'control' | 'moreShapesMenu' | 'shapesMenu',
    },
  },
  /**
   * Enforces a maximum limit of 100 operations on the undo/redo stack to prevent memory issues
   */
  undoRedoStackLimit: {
    experimentId: 'whiteboards_undo_redo_stack_limit',
    defaultParams: {
      enabled: false,
    },
  },
  /**
   * Controls confluence race mode (the racing minigame)
   * https://switcheroo.atlassian.com/ui/gates/ba1190fc-0902-4a27-beac-73348b01f108/key/confluence_race_mode
   */
  confluenceRaceMode: {
    experimentId: 'confluence_race_mode',
    defaultParams: {
      isEnabled: false,
    },
  },
  /**
   * Enables the preview panel for whiteboards
   * https://switcheroo.atlassian.com/ui/gates/c05f0d13-ade7-4ab8-9f28-422609eba6c5/key/preview_panel_for_whiteboards
   */
  whiteboardsPreviewPanel: {
    experimentId: 'preview_panel_for_whiteboards',
    defaultParams: {
      isEnabled: false,
    },
  },
  /**
   * Enables the preview panel for databases
   * https://switcheroo.atlassian.com/ui/gates/bf435ee6-e476-495a-99c4-f6e95672e5a8/key/preview_panel_for_databases
   */
  databasesPreviewPanel: {
    experimentId: 'preview_panel_for_databases',
    defaultParams: {
      isEnabled: false,
    },
  },
} satisfies Record<string, Experiment>;

type Experiment = {
  experimentId: string;
  defaultParams: Record<string, any>;
};

type ExperimentConfig = typeof experimentConfig;
export type ExperimentName = keyof ExperimentConfig;
type ExperimentParams = {
  [key in keyof ExperimentConfig]: ExperimentConfig[key]['defaultParams'];
};

type ExperimentOverrides = {
  [key in keyof ExperimentConfig]?: Partial<ExperimentConfig[key]['defaultParams']>;
};

export const experimentOverrides = proxy<ExperimentOverrides>({});

/**
 * Apply feature flag overrides to experiment values
 * This should be called during app initialization
 */
export const applyFeatureFlagOverridesToExperiments = (
  featureFlagOverrides: FeatureFlagOverrides
) => {
  // Reset any existing overrides
  Object.keys(experimentOverrides).forEach((key) => {
    delete experimentOverrides[key as ExperimentName];
  });

  Object.entries(featureFlagOverrides).forEach(([flagName, value]) => {
    if (isExperimentName(flagName)) {
      experimentOverrides[flagName] = {
        ...experimentOverrides[flagName],
        enabled: value,
      };
    }
  });
};

/**
 * Apply experiment parameter overrides from URL query parameters
 * This allows setting specific experiment parameters via ?ep=experimentName.paramName.value
 * This should be called during app initialization
 */
export const applyExperimentParamOverrides = (
  paramOverrides: Record<string, Record<string, any>>
) => {
  Object.entries(paramOverrides).forEach(([experimentName, params]) => {
    if (!isExperimentName(experimentName)) {
      console.warn(`Unknown experiment name in param overrides: "${experimentName}"`);
      return;
    }

    const config = experimentConfig[experimentName as ExperimentName];

    Object.entries(params).forEach(([paramName, value]) => {
      if (!(paramName in config.defaultParams)) {
        console.warn(
          `Unknown parameter "${paramName}" for experiment "${experimentName}"`
        );
        return;
      }

      experimentOverrides[experimentName as ExperimentName] = {
        ...experimentOverrides[experimentName as ExperimentName],
        [paramName]: value,
      };
    });
  });
};

/**
 * Defining our own type so we can mark `fireExperimentExposure` as required to
 * enforce explicit opt-in of firing exposure events.
 */
type ExperimentValueOptions<T> = {
  fireExperimentExposure: boolean;
} & GetExperimentValueOptions<T>;

/**
 * Returns the value of a given parameter for the provided experiment
 *
 * **NOTE**: You must explicitly set whether you want this call to fire an
 * exposure event or not. This is done with the `options` param (see {@link ExperimentValueOptions}).
 *
 * If you're unsure whether you should be firing an exposure event, reach out to
 * the team experimentation champions.
 *
 * This page explains the importance of exposure events: {@link https://hello.atlassian.net/wiki/spaces/~732385844/pages/3187295823/Exposure+Events+101}
 *
 * @example getExperimentValue('whiteboardAATest', 'enabled', { fireExperimentExposure: true })
 * @example getExperimentValue('anotherExperiment', 'btnColor', { fireExperimentExposure: false })
 */
export const getExperimentValue = <
  const ExpName extends ExperimentName,
  const ParamName extends keyof ExperimentParams[ExpName]
>(
  experimentName: ExpName,
  paramName: ParamName,
  /**
   * Statsig options for fetching the experiment value. `fireExposureEvent` is required
   */
  options: ExperimentValueOptions<ExperimentParams[ExpName][ParamName]>
): ExperimentParams[ExpName][ParamName] => {
  // Check if there's an override for this experiment and parameter
  if (
    experimentOverrides[experimentName] &&
    paramName in experimentOverrides[experimentName]
  ) {
    const override = experimentOverrides[experimentName];
    return override[
      paramName as keyof typeof override
    ] as ExperimentParams[ExpName][ParamName];
  }

  const { experimentId, defaultParams } = experimentConfig[experimentName];

  // Typescript is unable to discriminate between experiment params
  // This should be okay to cast as TypeScript will throw an error if we try to call this
  // function with a param name that doesn't exist for the provided experiment
  const defaultParam = (defaultParams as ExperimentParams[ExpName])[paramName];

  return FeatureGates.getExperimentValue(
    experimentId,
    paramName as string,
    defaultParam,
    options
  );
};

/**
 * Tells Statsig to manually log an exposure event, useful when you've already
 * evaluated an experiment but explicitly told it not to fire an experiment exposure
 *
 * Note that this is idempotent
 *
 * See: https://docs.statsig.com/client/jsClientSDK#manual-exposures-
 */
export const manuallyFireExposureEvent = (experimentName: ExperimentName) => {
  // Offline mode and test mode will crash since Statsig is never initialized
  if (process.env.MODE === 'offline' || process.env.MODE === 'test') {
    return;
  }

  const experimentId = experimentConfig[experimentName].experimentId;

  return FeatureGates.manuallyLogExperimentExposure(experimentId);
};

/**
 * Type guard to check if a string is a valid experiment name.
 */
export function isExperimentName(value: string): value is ExperimentName {
  return value in experimentConfig;
}

/**
 * Gets all experiment values without firing exposure events
 * This is intended for debugging purposes only
 */
export const getAllExperimentValues = () => {
  const result: Record<string, Record<string, any>> = {};

  Object.entries(experimentConfig).forEach(([experimentName, config]) => {
    result[experimentName] = {};

    // For each parameter in the experiment, get its value
    Object.keys(config.defaultParams).forEach((paramName) => {
      const value = getExperimentValue(
        experimentName as ExperimentName,
        paramName as never,
        { fireExperimentExposure: false }
      );

      result[experimentName][paramName] = value;
    });
  });

  return result;
};

/**
 * Native Database Embed feature flag utilities
 *
 * These utilities combine the overarching nativeDbEmbed gate with individual
 * milestone feature flags to determine if a specific feature should be enabled.
 *
 * Both the gate AND the specific feature flag must be enabled for the feature to be active.
 */

/**
 * Check if the Visual Refresh milestone features should be enabled.
 * Requires the nativeDbEmbed gate to be enabled.
 *
 * @returns true if the gate is enabled
 * @privateRemarks actively chose NOT to check a feature gate since it's the
 * first milestone and therefore you can infer that the it should be on if
 * nativeDbEmbed is on
 */
export const isNativeDbVisualRefreshEnabled = (): boolean => {
  const nativeDbEnabled = isFeatureFlagEnabled('nativeDbEmbed');

  return nativeDbEnabled;
};

/**
 * M1 - Visual Refresh: Database Embed Visual Update
 * Covers:
 * - Framing around the database element removed
 * - Improved drag functionality for database elements
 *
 * Check if the Toolbar/Action Bar milestone features should be enabled.
 * Requires both the nativeDbEmbed gate and wbDbToolbarActionBar flag to be enabled.
 * @returns true if both flags are enabled
 */
export const isNativeDbToolbarActionBarEnabled = (): boolean => {
  const nativeDbEnabled = isFeatureFlagEnabled('nativeDbEmbed');
  const toolbarActionBarEnabled = isFeatureFlagEnabled('wbDbToolbarActionBar');

  return nativeDbEnabled && toolbarActionBarEnabled;
};

/**
 * Check if the Flags/Snackbars/Modals milestone features should be enabled.
 * Requires both the nativeDbEmbed gate and wbDbFlagsSnackbarsModals flag to be enabled.
 *
 * @returns true if both flags are enabled
 */
export const isNativeDbFlagsSnackbarsModalsEnabled = (): boolean => {
  const nativeDbEnabled = isFeatureFlagEnabled('nativeDbEmbed');

  const flagsSnackbarsModalsEnabled = isFeatureFlagEnabled('wbDbFlagsSnackbarsModals');

  return nativeDbEnabled && flagsSnackbarsModalsEnabled;
};

/**
 * Check if the Hello Release Other milestone features should be enabled.
 * Requires both the nativeDbEmbed gate and wbDbHelloReleaseOther flag to be enabled.
 *
 * @returns true if both flags are enabled
 */
export const isNativeDbHelloReleaseOtherEnabled = (): boolean => {
  const nativeDbEnabled = isFeatureFlagEnabled('nativeDbEmbed');

  const helloReleaseOtherEnabled = isFeatureFlagEnabled('wbDbHelloReleaseOther');

  return nativeDbEnabled && helloReleaseOtherEnabled;
};

/**
 * Check if the Changeboarding milestone features should be enabled.
 * Requires both the nativeDbEmbed gate and wbDbChangeboarding flag to be enabled.
 *
 * @returns true if both flags are enabled
 */
export const isNativeDbChangeboardingEnabled = (): boolean => {
  const nativeDbEnabled = isFeatureFlagEnabled('nativeDbEmbed');

  const changeboardingEnabled = isFeatureFlagEnabled('wbDbChangeboarding');

  return nativeDbEnabled && changeboardingEnabled;
};
